import React from 'react';
import { useMutation, useQueryClient } from 'react-query';

import api from 'api';
import { StandardButton } from 'components/ui/Button';
import { useModal } from 'components/ui/Modals';
import Modal from 'components/ui/Modals/components/Modal';
import { SetErrorMessageType } from '../contexts/RunPayrollErrorContext';
import { MessageTypes, SetMessageType } from '../contexts/RunPayrollMessageContext';
import { FETCH_RUNPAYROLL_DATA } from '../utils/queries';

import { RunPayrollModalHeader } from './components/RunPayrollModalHeader';
import styles from './index.module.scss';

/**
 * Resuming an execution that stopped short of completing.
 *
 * The word "retry" is deliberately absent from every string here. On a payroll
 * screen it reads as "pay everyone again", which is the single thing an admin
 * looking at a half-finished run is most afraid of — so the copy says what
 * actually happens instead, including the part about nobody being paid twice.
 *
 * This is only ever reachable for a run parked on INSUFFICIENT FUNDS — the one
 * stop an org can clear themselves. Its workflow is genuinely still alive on
 * its timer, so resuming really does signal it to carry on from where it
 * stopped; nothing is re-dispatched and no fresh run is created.
 *
 * Every other way a run stops is an internal fault the org cannot fix, and on
 * almost all of them the workflow is already closed, so retrying would
 * SUPERSEDE — a fresh run re-dispatching money over today's cohort. Those show
 * a "contact support" notice and no button (see PayrollActions), and the lever
 * lives in the RetryPayrollExecutionRun superadmin command instead. That is why
 * this copy can promise continuation without hedging.
 */
const ResumeExecution = ({
  payrollMonth,
  onError,
  showMessage,
}: {
  payrollMonth: string;
} & SetErrorMessageType &
  SetMessageType) => {
  const { closeModal } = useModal();
  const queryClient = useQueryClient();

  const resume = useMutation(
    (month: string) => api.runPayroll.resumePayrollExecution(month),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FETCH_RUNPAYROLL_DATA);
        showMessage && showMessage({ type: MessageTypes.EXECUTION_RESUMED });
        closeModal();
      },
      onError: (error) => {
        onError && onError({ message: (error as Error).message });
        closeModal();
      },
    },
  );

  return (
    <Modal>
      <div className={`flex flex-col max-w-xl ${styles['run-payroll-modal']}`}>
        <RunPayrollModalHeader>Resume Payroll Execution</RunPayrollModalHeader>
        <div className={`${styles['run-payroll-modal-content']}`}>
          {/*
            Cause-neutral on purpose. The resume button is offered only for
            REQUIRES_ADMIN_ACTION (insufficient balance) today — see
            RunPayrollLib::ADMIN_RESUMABLE_EXECUTION_STATUSES — but naming that
            cause here makes the copy silently wrong the day a second
            admin-clearable stop is added on the Compute side.
          */}
          This payroll started executing but did not finish. Resuming picks it up from where it
          stopped.
          <br />
          <br />
          Employees who have already been paid for this month will not be paid again — only the
          remaining ones are processed.
          <br />
          <br />
          It can take a few hours to complete. You will receive an email notification once it does.
        </div>
        <div className={`flex justify-end ${styles['run-payroll-modal-footer']} gap-6`}>
          <StandardButton
            onClick={() => resume.mutate(payrollMonth)}
            isPending={resume.isLoading}
            disabled={resume.isLoading}>
            RESUME EXECUTION
          </StandardButton>
          <StandardButton onClick={() => closeModal()} disabled={resume.isLoading}>
            CANCEL
          </StandardButton>
        </div>
      </div>
    </Modal>
  );
};

export default ResumeExecution;
